import React from 'react';

import { sanitize } from '~commonInfra/utils/Sanitize';
import { SiteUrlsProps } from '~src/bootstrap/redux/selectors/SiteUrlsSelectors';
import { isUsageMetricInfo } from '~src/metrics/models/IMetricInfo';
import { SpaceInfo } from '~src/spacesPlatform/state/SpaceInfo';

import { Formatters } from '../../../codegen/Localize';
import { L10n } from '../../../commonInfra/L10n/L10n';
import { DraftsById } from '../../../drafts/types/DraftsTypes';
import { getContentSpaceDefinition } from '../../contentDefinitionInterfaces/ContentDefinitionsLists';
import { isRecommendationType, isSpaceInfoItemType } from '../../models/ContentType';
import { IContentInfo } from '../../models/IContentInfo';
import { IFavoritableItemInfo } from '../../models/resourceInfoParts';
import { TextileHelper } from '../../utils/TextileHelper';
import { ActionsMenu } from '../ActionsMenu/ActionsMenu';
import { SpacesSiteUrls } from '../spaceComponents/SpacesSiteUrls';

export interface ContentCardProps extends SiteUrlsProps {
  readonly content: IContentInfo;
  readonly selected: boolean;
  readonly hoveredOrSelected?: boolean;
  readonly forDragPreview: boolean;
  readonly addContentFavorite: (item: IFavoritableItemInfo) => any;
  readonly removeContentFavorite: (item: IFavoritableItemInfo) => any;
  readonly getContentDetail: (content: IContentInfo) => void;
  readonly inWorkbookDatasourcesSubspace: boolean;
  readonly inDatasourceConnectionsSubspace: boolean;
  readonly inProjectSpace?: boolean;
  readonly inRecentsSpace?: boolean;
  readonly inPersonalSpace?: boolean;
  readonly allowDragAndDrop: boolean;
  readonly showActionMenu: boolean;
  readonly showLastAccessedAt?: boolean;
  readonly disableTooltip: boolean;
  readonly drafts?: DraftsById;
  readonly spaceInfo?: SpaceInfo;
}

export const maxDescriptionLengthWithDraft = 48;

export abstract class ContentCard<Props extends ContentCardProps = ContentCardProps, State = {}>
  extends React.PureComponent<Props, State> {

    protected getDescription() {
      return TextileHelper.getPlainTextFromTextile(this.props.content.description, sanitize);
    }

    protected getDescriptionWithDraft() {
    let descriptionPlaintext = TextileHelper.getPlainTextFromTextile(this.props.content.description, sanitize);
    return L10n.elideIfLong(descriptionPlaintext, maxDescriptionLengthWithDraft);
    }

    protected getLastAccessedAt() {
      return this.props.content.lastAccessedAt ? Formatters.formatLongBestFitRelativeTime(new Date(this.props.content.lastAccessedAt)) : '';
    }

    protected renderActionsMenu(): JSX.Element {
      return this.props.showActionMenu &&
        <ActionsMenu
          targetItems={[ this.props.content ]}
          disabled={ this.props.selected }
          useIcon={true}/>;
    }

    protected handleShowTooltip = () => {
      this.props.getContentDetail(this.props.content);
    };
}

export function getContentUrlForItem(content: IContentInfo, urls: SpacesSiteUrls): string | undefined {
  let contentType = content.contentType;
  if (isRecommendationType(contentType)) {
    contentType = 'view';
  }

  // it only makes sense to generate a url for items that have their own space
  // and hence are SpaceInfoItemType('s)
  if (isSpaceInfoItemType(contentType)) {
    if (isUsageMetricInfo(content)) {
      // usage metrics have a special url that requires workbook id as well as metric id
      return urls.workbookUsageMetricUrl({ workbookId: content.workbookId, usageMetricId: content.id });
    }
    const contentDefinition = getContentSpaceDefinition(contentType);

    return contentDefinition.contentSpaceUrl(urls, content.id, content.luid);
  }
}
